import { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';

//elements
import { NumberInput } from '../../../../../elements/Input';
import Select from '../../../../../elements/Select.js';
import SwitchWidget from '../../SwitchWidget';
import CustomMultiSelect from '../../../../../elements/CustomMultiSelect.js';

//utils
import { CLIENT_RULE_CHANGE_VALIDATORS, DEFAULT_ABSENCE_TYPES } from '../../../../../utils/constants.js';
import { translateLabels } from '../../../../../utils/commonMethods.js';

const AbsenceSettings = (props) => {
    const { t, commonStore, clientStore, handleUserDataChange, handleClientRuleChange } = props;
    const { currentEntity } = clientStore;
    const { basicRules, extendedRules } = currentEntity.client.data;
    let absenceTypesOptions = extendedRules.absent_types || [];
    let multiSelectAbsenceTypesOptions = extendedRules.absent_types || [];
    if (absenceTypesOptions?.length) {
        absenceTypesOptions = absenceTypesOptions.map(element => ({ value: element.code, label: element.name, key: element.name }));
        multiSelectAbsenceTypesOptions = multiSelectAbsenceTypesOptions.map((element, index) => ({ value: index, code: element.code, label: element.name, key: element.name }));
        absenceTypesOptions.unshift({ value: 'all', label: 'All', key: 'All' });
    }

    translateLabels(absenceTypesOptions, t, 'label');

    const [selfClarificationCycleInMonths, setSelfClarificationCycleInMonths] = useState(basicRules.selfClarificationCycleInMonths);
    const [selfClarificationPeriod, setselfClarificationPeriod] = useState(basicRules.selfClarificationPeriod);
    const [selfClarificationPeriodsInCycle, setselfClarificationPeriodsInCycle] = useState(basicRules.selfClarificationPeriodsInCycle);
    const [selfClarificationDaysInPeriod, setselfClarificationDaysInPeriod] = useState(basicRules.selfClarificationDaysInPeriod);
    const [allowAbsenceForFixedSalariedEmployees, setAllowAbsenceForFixedSalariedEmployees] = useState(basicRules.allowAbsenceForFixedSalariedEmployees);
    const [requiredEmploymentMonthsForAbsence, setRequiredEmploymentMonthsForAbsence] = useState(basicRules.requiredEmploymentMonthsForAbsence);
    const [absenceTypesWithMandatoryEmploymentCheck, setabsenceTypesWithMandatoryEmploymentCheck] = useState(basicRules.absenceTypesWithMandatoryEmploymentCheck);
    const [IASickLeavesEnabled, setIASickLeavesEnabled] = useState(basicRules.IASickLeavesEnabled);
    const [IASickLeaveMaxDaysInPeriod, setIASickLeaveMaxDaysInPeriod] = useState(basicRules.IASickLeaveMaxDaysInPeriod);
    const [IASickLeaveMaxDaysInCycle, setIASickLeaveMaxDaysInCycle] = useState(basicRules.IASickLeaveMaxDaysInCycle);
    const [isIASickLeaveEditable, setIsIASickLeaveEditable] = useState(false);
    const [allowWorkRelatedQueriesForAbsence, setAllowWorkRelatedQueriesForAbsence] = useState(basicRules.allowWorkRelatedQueriesForAbsence);
    const [selectedWorkRelatedAbsenceTypes, setSelectedWorkRelatedAbsenceTypes] = useState(basicRules.workRelatedAbsenceTypes || []);
    const [followScheduleHoursForAbsence, setScheduleHoursForAbsence] = useState(basicRules.followScheduleHoursForAbsence);
    const [allowRegisteringAbsenceWithMandatoryTimesheets, setAllowRegisteringAbsenceWithMandatoryTimesheets] = useState(basicRules.allowRegisteringAbsenceWithMandatoryTimesheets);

    const handleIASickLeavesEdit = async () => {
        try {
            const res = await clientStore.validateClientRuleChange({ changeType: CLIENT_RULE_CHANGE_VALIDATORS.ABSENCE_SICK_LEAVE_TYPE_CHANGE }, 'absence');
            const { hasError, message } = res;
            if (hasError) return commonStore.addNotification(t(message), null, 'error', 'top-right', 3000);
            setIsIASickLeaveEditable(!isIASickLeaveEditable);
        }
        catch (error) {
            console.error(error);
        }
    }

    const handleIARuleChange = (checked) => {
        const { currentEntity } = props.clientStore;
        const extendedRules = Object.assign({}, currentEntity.client.data.extendedRules);
        const absenceTypes = extendedRules.absent_types.filter(e => e.code !== (checked ? DEFAULT_ABSENCE_TYPES.SICK_LEAVE : DEFAULT_ABSENCE_TYPES.IA_SICK_LEAVE));
        const sickLeaveType = checked ? { code: DEFAULT_ABSENCE_TYPES.IA_SICK_LEAVE, name: 'IA Sick Leave' } :
            { code: DEFAULT_ABSENCE_TYPES.SICK_LEAVE, name: 'Sick Leaves' };
        absenceTypes.unshift(sickLeaveType);
        extendedRules.absent_types = absenceTypes;
        setIASickLeavesEnabled(checked);
        handleClientRuleChange('extendedRules', extendedRules);
        handleUserDataChange('IASickLeavesEnabled', checked);
        const message = checked ? 'IA-Sick-leave absence type is added to absence types, please configure the wagetype if required' : 'IA-Sick-leave absence type is removed from absence types';
        commonStore.addNotification(t(message), null, 'success', 'top-right', 3000);
    }

    return (
        <>
            <Row>
                <Col xs={6}>
                    <Row className='mt-2'>
                        <label className="control-label font-size-15 mt-2 font-color-primary">{t('Cycle settings')}</label>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Sick Leave Cycle')} ({t('In Months')})
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("The standard cycle for sick leave is by default for 12 months, starting from the first sick leave registered. It resets automatically once the cycle is completed")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="selfClarificationCycleInMonths"
                                value={selfClarificationCycleInMonths}
                                disabled
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setSelfClarificationCycleInMonths(value);
                                    handleUserDataChange('selfClarificationCycleInMonths', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Self clarification period days')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("The length of the sick-leave period is by default for 16 days within which the employee can take maximum allowed sick-leaves")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="selfClarificationPeriod"
                                value={selfClarificationPeriod}
                                disabled
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setselfClarificationPeriod(value);
                                    handleUserDataChange('selfClarificationPeriod', value);
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={6}>
                    <Row className='mt-2'>
                        <label className="control-label font-size-15 mt-2 font-color-primary">{t('Standard Sick-leave')}</label>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Maximum number of periods')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("Maximun number of sick-leave periods in a cycle, as per standard rules it is set to 4")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="selfClarificationPeriodsInCycle"
                                value={selfClarificationPeriodsInCycle}
                                disabled
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setselfClarificationPeriodsInCycle(value);
                                    handleUserDataChange('selfClarificationPeriodsInCycle', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Maximum sick-leave days in a period')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("By default employee can apply upto 3 days of sick-leave per period, update this if required however please not it would also effect the total number of sick-leaves in a cycle")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="selfClarificationDaysInPeriod"
                                value={selfClarificationDaysInPeriod}
                                disabled
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setselfClarificationDaysInPeriod(value);
                                    handleUserDataChange('selfClarificationDaysInPeriod', value);
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col xs={6}>
                    <Row className='mt-2'>
                        <label className="control-label font-size-15 mt-2 font-color-primary">{t('Employment Rules')}</label>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Fixed Salaried employees only')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("By default all the employees are eligible for sick leave. If you want to limit it to fixed salaried employees, select this option")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <SwitchWidget
                                value={allowAbsenceForFixedSalariedEmployees}
                                onChange={checked => {
                                    setAllowAbsenceForFixedSalariedEmployees(checked);
                                    handleUserDataChange('allowAbsenceForFixedSalariedEmployees', checked);
                                }} />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Required months of employment')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("Minimum months of employment before an employee can apply for absence, by default 2 months")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="requiredEmploymentMonthsForAbsence"
                                value={requiredEmploymentMonthsForAbsence}
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setRequiredEmploymentMonthsForAbsence(value);
                                    handleUserDataChange('requiredEmploymentMonthsForAbsence', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Check required employment for absence types')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("By default the minimum months of employment for absence is checked for all absence types. If you want to limit it to specific absence types, please select accordingly")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <Select
                                menuPlacement={"top"}
                                options={absenceTypesOptions}
                                value={absenceTypesOptions.find(item => item.value === absenceTypesWithMandatoryEmploymentCheck)}
                                onChange={evt => {
                                    setabsenceTypesWithMandatoryEmploymentCheck(evt.value);
                                    handleUserDataChange('absenceTypesWithMandatoryEmploymentCheck', evt.value);
                                }}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={6}>
                    <Row className='mt-2 justify-content-space-between'>
                        <Col xs={10}>
                            <label className="control-label font-size-15 mt-2 font-color-primary">{t('IA company sick leave')}</label>
                        </Col>
                        <Col xs={2}>
                            <OverlayTrigger
                                placement="left"
                                overlay={
                                    <Tooltip id="Edit-IASickLeaves">
                                        {t("Enable edit mode")}
                                    </Tooltip>
                                }
                            >
                                <i
                                    className="fa fa-edit mt-2 font-size-15 cursor-pointer font-color-primary"
                                    onClick={handleIASickLeavesEdit}
                                />
                            </OverlayTrigger>
                        </Col>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Enable IA sick-leave')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("By default the standard sick-leaves are used by the system, to replace them with IA sick-leave please select this option")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <SwitchWidget
                                value={IASickLeavesEnabled}
                                disabled={!isIASickLeaveEditable}
                                onChange={(checked) => handleIARuleChange(checked)}
                            />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Maximum sick-leave days in a period')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("Maximum number of sick-leave days in a period, as per standard rules it is set to 8")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="IASickLeaveMaxDaysInPeriod"
                                value={IASickLeaveMaxDaysInPeriod}
                                disabled={!isIASickLeaveEditable}
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setIASickLeaveMaxDaysInPeriod(value);
                                    handleUserDataChange('IASickLeaveMaxDaysInPeriod', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='align-items-center mb-1'>
                        <Col xs={8}>
                            <label className="control-label">{t('Total number of allowed sick-leaves')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("Total number of sick-leave days in a cycle, as per standard rules it is set to 24")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={4}>
                            <NumberInput
                                name="IASickLeaveMaxDaysInCycle"
                                disabled={!isIASickLeaveEditable}
                                value={IASickLeaveMaxDaysInCycle}
                                onChange={evt => {
                                    const value = parseInt(evt.target.value, 10);
                                    setIASickLeaveMaxDaysInCycle(value);
                                    handleUserDataChange('IASickLeaveMaxDaysInCycle', value);
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <hr />
            <Row>
                <Col xs={6}>
                    <Row >
                        <label className="control-label font-size-15 mt-2 font-color-primary">{t('Work Related Queries')}</label>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={7}>
                            <label className="control-label">{t('Allow work related queries')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("If enabled then work related field will be visible in absence form and reports according to selected absence types")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={5}>
                            <SwitchWidget
                                value={allowWorkRelatedQueriesForAbsence}
                                onChange={checked => {
                                    setAllowWorkRelatedQueriesForAbsence(checked);
                                    handleUserDataChange('allowWorkRelatedQueriesForAbsence', checked);
                                }} />
                        </Col>
                    </Row>

                    <Row className='align-items-center mb-1'>
                        <Col xs={7}>
                            <label className="control-label">{t('Work related absence types')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("Select absence types for which work related field will be visible in absence form and reports")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={5}>
                            <CustomMultiSelect
                                options={multiSelectAbsenceTypesOptions}
                                value={selectedWorkRelatedAbsenceTypes.map(code =>
                                    multiSelectAbsenceTypesOptions.find(opt => opt.code === code).value
                                )}
                                onChange={(selected) => {
                                    const newSelectedCodes = selected.map(index =>
                                        multiSelectAbsenceTypesOptions[index]?.code
                                    ).filter(Boolean);

                                    setSelectedWorkRelatedAbsenceTypes(newSelectedCodes);
                                    handleUserDataChange('workRelatedAbsenceTypes', newSelectedCodes);
                                }}
                                className={"custom-multi-select-max-height-150"}
                                overrideStrings={{
                                    search: t('Search'),
                                    selectAll: t('Select All'),
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xs={6}>
                    <Row >
                        <label className="control-label font-size-15 mt-2 font-color-primary">{t('Hours in Absence')}</label>
                    </Row>
                    <hr />
                    <Row className='align-items-center mb-1'>
                        <Col xs={7}>
                            <label className="control-label">{t('Follow Schedule for Absence Hours')}
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("When enabled, absence hours follow the employee’s work schedule. When disabled, absence hours are calculated using the primary shift or standard company hours.")}.
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </label>
                        </Col>
                        <Col xs={5}>
                            <SwitchWidget
                                value={followScheduleHoursForAbsence}
                                onChange={checked => {
                                    setScheduleHoursForAbsence(checked);
                                    handleUserDataChange('followScheduleHoursForAbsence', checked);
                                }} />
                        </Col>
                    </Row>
                    {basicRules.allowManagingTimelogsManually && (
                        <Row className='align-items-center mb-1'>
                            <Col xs={7}>
                                <label className="control-label">{t('Allow employees to register absence with mandatory timesheets')}
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip id="tooltip-top">
                                                {t("When enabled, employees can register absence even if they have a mandatory timesheet and vice versa.")}.
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={5}>
                                <SwitchWidget
                                    value={allowRegisteringAbsenceWithMandatoryTimesheets}
                                    onChange={checked => {
                                        setAllowRegisteringAbsenceWithMandatoryTimesheets(checked);
                                        handleUserDataChange('allowRegisteringAbsenceWithMandatoryTimesheets', checked);
                                    }} />
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
        </>
    )
}
export default inject('clientStore', 'commonStore')(withLocalization(observer(AbsenceSettings)));
